<template>
  <Loader
    :key="category"
    :value="promises.fetch"
    :message="$t('loading', { resource }) + '...'"
    :class="`pages pages--category-${category}`"
  >
    <div v-if="enums.PageLists" class="container view">
      <HeaderComponent :title="$metaInfo.title">
        <template #actions>
          <Actions placement="pages" />
          <ButtonComponent
            v-if="createButton && $store.getters.hasAccess('admin')"
            tag="router-link"
            :to="createButton.to"
            :label="createButton.label"
            :icon="createButton.icon"
            :theme="createButton.theme"
          />
        </template>
      </HeaderComponent>

      <CustomPageList v-if="customListConfig && customListConfig.items" v-bind="customListConfig" :title="resource" />

      <template v-else>
        <div class="row between bottom mb-2">
          <FilterComponent
            v-model="filter"
            :items="filterItems"
            :default="defaultFilter"
            :options="{
              search: { placeholder: [`${$t('name')}, ${$t('street')}`] },
              includeArchived: { label: $t('showArchived') },
              hideDemo: { label: $t('hideDemo') },
            }"
          />
          <ButtonGroup class="pages__view-toggle">
            <ButtonComponent
              v-tooltip="$t('listView')"
              :icon="['fal', 'list']"
              @click="setViewType('list')"
              :data-active="viewType === 'list'"
            />
            <ButtonComponent
              v-tooltip="$t('gridView')"
              :icon="['fal', 'th-large']"
              @click="setViewType('grid')"
              :data-active="viewType === 'grid'"
            />
          </ButtonGroup>
        </div>

        <div v-if="items.length || states.fetch" :class="['pages__content', `pages__content--${viewType}`]">
          <template v-if="viewType === 'grid'">
            <Card v-for="item in items" :default-lang="defaultLang" :key="item.id" :value="item"></Card>
          </template>
          <template v-else-if="viewType === 'list'">
            <PagesList v-bind="listOptions" />
          </template>
        </div>

        <EmptyState v-else :title="$t('noX', { x: resource })" :icon="['fal', 'browser']" action-portal />
      </template>
    </div>
  </Loader>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'

import HeaderComponent from '@/components/Header'
import Card from '@/components/Page/Card'
import Actions from '@/components/Actions'
import { CheckboxComponent } from 'vue-elder-checkbox'
import EmptyState from '@/components/EmptyState'
import ButtonGroup from '@kvass/button-group'
import FilterComponent from '@/components/Filter/Component'
import FilterMixin from '@/components/Filter'
import { Debounce } from '@/utils'
import PagesList from '@/components/Page/List/Component'
import CustomPageList from '@/components/CustomFields/ProjectList'
import tracker from '@/tracking'

const storageKey = `${encodeURI(window.location.hostname)}:pages:viewType`

const hideDemoKey = `${encodeURI(window.location.hostname)}:hideDemo`

export default {
  mixins: [FilterMixin({ default: { search: '', includeArchived: false, hideDemo: false } })],
  watch: {
    filter: {
      handler: 'fetch',
      deep: true,
      immediate: true,
    },
    'filter.hideDemo'(value) {
      if (!value) return localStorage.removeItem(hideDemoKey)
      localStorage.setItem(hideDemoKey, 'true')
    },
    '$route.query': {
      handler(value) {
        if (!value.category) this.$router.push({ query: { category: this.category } })
      },
      immediate: true,
    },
  },

  data() {
    return {
      viewType: localStorage.getItem(storageKey) || 'grid',
    }
  },
  computed: {
    ...mapGetters('Page', ['getCategory']),
    ...mapState('Page', ['items', 'promises', 'states']),
    ...mapState(['enums', 'hasDemoContent']),
    filterItems() {
      const items = ['search', 'checkbox:includeArchived']
      if (!this.hasDemoContent) return items

      return [...items, 'checkbox:hideDemo']
    },
    category() {
      return (
        this.$route.query.category ||
        (JSON.parse(sessionStorage.getItem(`page:category`)) || {}).id ||
        this.getCategory().id
      )
    },
    defaultLang() {
      if (!this.enums) return
      return ((this.enums.Languages || []).find(i => Boolean(i.default)) || {}).value
    },
    resource() {
      return (this.$metaInfo.title || '').toLowerCase()
    },

    createButton() {
      const category = (this.enums.PageCategories || []).find(c => c.id === this.category) || {}
      const create = category.create

      const config = create || {}
      return {
        to: config.to || { name: 'page-create', query: this.$route.query },
        label: config.label || `${this.$t('create')} ${this.resource}`,
        icon: config.icon || ['fal', 'plus'],
        theme: config.theme || 'primary',
      }
    },

    customListConfig() {
      const pageLists = this.enums.PageLists || []
      const pageList = pageLists.find(i => i.category === this.category)
      if (!pageList) return

      return {
        parentIsActive: true,
        selectable: false,
        wrappers: this.listOptions.wrappers,
        ...(pageList || {}),
      }
    },
    listOptions() {
      return {
        value: this.items,
        config: [
          {
            id: 'name',
            source: 'metadata.addons',
            component: 'Name',
            label: this.$t('name'),
            size: '1fr',
          },
          {
            id: 'demo',
            component: 'Badge',
            label: this.$tc('tag', 2),
            size: '1fr',
            theme: 'warning',
            type: 'outlined',

            placeholder: this.$t('demo'),
          },
        ],
        wrappers: {
          row: page => ({
            component: 'router-link',
            props: {
              'data-page-id': page.id,
              to: { name: 'page-dashboard', params: { id: page.id }, query: { category: this.category } },
              style: { color: 'inherit' },
            },
          }),
          cell: (page, column) => ({
            component: 'div',
            props: { 'data-column-id': column.id },
          }),
        },
      }
    },
  },
  methods: {
    ...mapActions('Page', {
      fetch: Debounce(function (dispatch) {
        if (Object.values(this['filter']).filter(v => (Array.isArray(v) ? v.length : Boolean(v))).length)
          tracker.emit(`Page:filter`, this['filter'])

        return dispatch('fetch', { filter: { ...this.filter, categories: [this.category || 'default'] } })
      }, 200),
    }),

    setViewType(viewType) {
      this.viewType = viewType
      localStorage.setItem(storageKey, viewType)
    },
  },
  metaInfo() {
    let title = this.$store.getters['Page/getCategory'](this.category).label

    return {
      title: title === 'page' ? this.$tc('page', 2) : title,
    }
  },

  created() {
    if (this.hasDemoContent && localStorage.getItem(hideDemoKey) === 'true') this.filter.hideDemo = true
  },

  components: {
    Card,
    HeaderComponent,
    Actions,
    CheckboxComponent,
    EmptyState,
    ButtonGroup,
    FilterComponent,
    PagesList,
    CustomPageList,
  },
}
</script>

<style lang="scss">
.pages {
  background-color: $background-grey;

  &__content {
    &--grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      grid-gap: $form-gap;

      & > * {
        animation: fadeIn 300ms ease;
      }
    }
  }
  &__view-toggle {
    .elder-button {
      &__icon {
        padding: 0.5em 0.75em !important;
      }

      &[data-active='true'] {
        box-shadow: inset 0 3px 6px rgba(black, 0.1);
        color: var(--primary);
      }
    }
  }
}
</style>
